import { useState } from 'react'
import { formatScore } from '../../core/index.js'
import type { PeriodClose, TierChangeReason } from '../index.js'
import { useCloseFacilityPeriod } from '../index.js'
import { TIER_LABELS } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { CalendarCheck } from 'lucide-react'

const REASON_TEXT: Record<TierChangeReason, string> = {
  score: 'from the score',
  new_hire: 'new hire: probationary until the first 90 days are up',
  provisional_held: 'held: a provisional score may not lower a tier',
  no_score: 'nothing to score this period',
  no_thresholds: 'no tier thresholds configured',
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** The month before the current one, which is what a close with no period named closes. */
function lastEndedMonth(): string {
  const now = new Date()
  const previous = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
  return previous.toISOString().slice(0, 7)
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

/** "September 2026" for 2026-09: a button that reads "Close 2026-09…" looks cut off. */
function monthName(period: string): string {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** The close's result: how many moved, then every worker's final and what it did to their tier. */
export function PeriodCloseResult({ close }: { close: PeriodClose }) {
  const { summary } = close
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-mid">
        <span className="font-bold text-ink">
          {close.period.from.slice(0, 7)} {close.alreadyClosed ? 'was already closed' : 'closed'} {formatDateTime(close.closedAt)}
        </span>
        <span>
          <span className="font-bold text-green">{summary.up} up</span> · <span className="font-bold text-coral">{summary.down} down</span> · {summary.closed - summary.moved} unchanged
        </span>
        {summary.newHires > 0 && <span>{summary.newHires} new {summary.newHires === 1 ? 'hire' : 'hires'} kept probationary</span>}
        {summary.held > 0 && <span>{summary.held} held on a provisional score</span>}
        {summary.unscored > 0 && <span>{summary.unscored} with nothing to score</span>}
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            <th className="py-1 pr-2 font-bold">Worker</th>
            <th className="py-1 pr-2 font-bold text-right">Final</th>
            <th className="py-1 pr-2 font-bold">Tier</th>
            <th className="py-1 font-bold">Why</th>
          </tr>
        </thead>
        <tbody>
          {close.workers.map((worker) => (
            <tr key={worker.workerId} className="border-t border-marine/10">
              <td className="py-1.5 pr-2 text-ink">{worker.name}</td>
              <td className="py-1.5 pr-2 text-right font-bold text-ink">
                {worker.score === null ? '—' : formatScore(worker.score)}
                {worker.provisional && <span className="ml-1 font-normal text-ink-mid">prov.</span>}
              </td>
              <td className="py-1.5 pr-2">
                {worker.previousTier === worker.tier ? (
                  <span className="text-ink-mid">{TIER_LABELS[worker.tier]}</span>
                ) : (
                  <span className="font-bold text-ink">
                    <span className="font-normal text-ink-mid">{TIER_LABELS[worker.previousTier]} → </span>
                    {TIER_LABELS[worker.tier]}
                  </span>
                )}
              </td>
              <td className="py-1.5 text-[10px] text-ink-mid">{REASON_TEXT[worker.reason]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Closes a scoring period: a final score per worker, the tier each final
 * implies under the facility's rules, and the new period's reset and bid
 * window dates. The one action that changes tiers, so it asks first.
 */
export function ClosePeriodCard({ facilityId, hasWorkers = true }: { facilityId: string; /** False for an empty roster, which leaves nothing to close. */ hasWorkers?: boolean }) {
  const close = useCloseFacilityPeriod(facilityId)
  const [isConfirming, setIsConfirming] = useState(false)
  const [closeCurrent, setCloseCurrent] = useState(false)
  const period = closeCurrent ? currentMonth() : lastEndedMonth()

  return (
    <div className="bg-white border border-marine/15 rounded p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-black text-ink">Close a period</div>
          <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">
            Computes every worker's final score for the period and sets their tier from it: a new hire stays probationary for their first 90 days, a
            provisional score never lowers a tier, and a manual override is replaced. Bid windows for the next period follow the new tiers.
          </p>
        </div>
        {!isConfirming && (
          <Button icon={CalendarCheck} variant="secondary" disabled={close.isPending || !hasWorkers} title={hasWorkers ? undefined : 'No workers on the roster yet'} onClick={() => setIsConfirming(true)}>
            Close {monthName(lastEndedMonth())}
          </Button>
        )}
      </div>
      {!hasWorkers && <p className="text-[11px] text-ink-mid">No workers on the roster yet, so there is nothing to close.</p>}

      {isConfirming && (
        <div className="border border-gold/60 bg-gold-lt rounded p-3 flex flex-col gap-2 text-[11px] text-ink">
          <div className="font-bold">Close {monthName(period)} for every worker at this facility?</div>
          <div>Tiers and bid windows change for the whole roster. A period already closed is returned as it was stored.</div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={closeCurrent} onChange={(event) => setCloseCurrent(event.target.checked)} />
            <span>
              Close the current month ({monthName(currentMonth())}) early instead <span className="text-ink-mid">· for a demonstration; the running scores stop mattering once a final exists</span>
            </span>
          </label>
          <div className="flex items-center gap-3">
            <Button
              icon={CalendarCheck}
              disabled={close.isPending}
              onClick={() => {
                close.mutate(closeCurrent ? { period: currentMonth(), force: true } : {}, { onSettled: () => setIsConfirming(false) })
              }}
            >
              {close.isPending ? 'Closing…' : `Close ${monthName(period)}`}
            </Button>
            <button type="button" disabled={close.isPending} onClick={() => setIsConfirming(false)} className="text-xs font-bold text-ink-mid hover:text-ink">
              Cancel
            </button>
          </div>
        </div>
      )}

      {close.isError && (
        <p role="alert" className="text-xs font-bold text-coral">
          {close.error instanceof Error && close.error.message ? close.error.message : 'The close failed.'}
        </p>
      )}
      {close.data && <PeriodCloseResult close={close.data} />}
    </div>
  )
}
